/**
 * A plain-text line diff, for the conflict dialog only (`112-offline-drafts.md` §7.3).
 *
 * **This is for DISPLAY.** It is not a merge, and there will not be one: automatic merging
 * of rich-text documents without CRDTs is how you silently corrupt both versions, and
 * CRDTs are explicitly out of scope (`000-overview.md`). The user picks a side.
 *
 * A textbook LCS walk — the inputs are two versions of one page, so the quadratic table is
 * a non-issue at this size.
 */
export type DiffOp = 'equal' | 'add' | 'remove';

export interface DiffLine {
  op: DiffOp;
  text: string;
}

export function diffLines(before: string, after: string): DiffLine[] {
  const a = before.split('\n');
  const b = after.split('\n');

  // lcs[i][j] = length of the longest common subsequence of a[i..] and b[j..]
  const lcs: number[][] = Array.from({ length: a.length + 1 }, () =>
    new Array<number>(b.length + 1).fill(0),
  );

  for (let i = a.length - 1; i >= 0; i--) {
    for (let j = b.length - 1; j >= 0; j--) {
      const row = lcs[i];
      const nextRow = lcs[i + 1];
      if (row === undefined || nextRow === undefined) continue;

      row[j] =
        a[i] === b[j] ? (nextRow[j + 1] ?? 0) + 1 : Math.max(nextRow[j] ?? 0, row[j + 1] ?? 0);
    }
  }

  const out: DiffLine[] = [];
  let i = 0;
  let j = 0;

  while (i < a.length && j < b.length) {
    if (a[i] === b[j]) {
      out.push({ op: 'equal', text: a[i] ?? '' });
      i++;
      j++;
    } else if ((lcs[i + 1]?.[j] ?? 0) >= (lcs[i]?.[j + 1] ?? 0)) {
      out.push({ op: 'remove', text: a[i] ?? '' });
      i++;
    } else {
      out.push({ op: 'add', text: b[j] ?? '' });
      j++;
    }
  }

  while (i < a.length) out.push({ op: 'remove', text: a[i++] ?? '' });
  while (j < b.length) out.push({ op: 'add', text: b[j++] ?? '' });

  return out;
}
